'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { LogOut, CheckCircle, XCircle, Calendar, Utensils, Users, Pencil } from 'lucide-react';
import RSVPForm from '@/app/rsvp-page/components/RSVPForm';
import RSVPEditForm from './RSVPEditForm';

interface PlusOne {
  name: string;
  dietary: string;
  dietaryOther?: string;
}

interface RSVPData {
  id: string;
  attending: string;
  primary_name: string;
  primary_email: string | null;
  primary_dietary: string;
  message: string | null;
  plus_ones: PlusOne[];
  friday_evening: boolean;
  sunday_brunch: boolean;
  sunday_walk: boolean;
  submitted_at: string;
}

interface GuestDashboardViewProps {
  user: any;
  onSignOut: () => void;
}

const dietaryLabel = (val: string, other?: string) => {
  if (!val || val === 'none') return 'No requirements';
  if (val === 'nut-allergy') return 'Nut Allergy';
  if (val === 'gluten-free') return 'Gluten Free';
  if (val === 'vegetarian') return 'Vegetarian';
  if (val === 'vegan') return 'Vegan';
  if (val === 'other') return other ? other : 'Other';
  return val;
};

export default function GuestDashboardView({ user, onSignOut }: GuestDashboardViewProps) {
  const [rsvp, setRsvp] = useState<RSVPData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const supabase = createClient();

  const fetchRSVP = useCallback(async () => {
    setLoading(true);
    if (!user?.email) { setLoading(false); setNotFound(true); return; }

    const email = user.email.trim().toLowerCase();

    // Try case-insensitive match first
    const { data, error } = await supabase
      .from('rsvp_submissions')
      .select('*')
      .ilike('primary_email', email)
      .maybeSingle();

    if (!error && data) {
      setRsvp(data);
      setNotFound(false);
      setLoading(false);
      return;
    }

    // Fallback: try exact match with original email (handles edge cases)
    const { data: data2, error: error2 } = await supabase
      .from('rsvp_submissions')
      .select('*')
      .eq('primary_email', user.email.trim())
      .maybeSingle();

    if (!error2 && data2) {
      setRsvp(data2);
      setNotFound(false);
    } else {
      setNotFound(true);
      setRsvp(null);
    }
    setLoading(false);
  }, [user?.email]);

  useEffect(() => {
    fetchRSVP();
  }, [fetchRSVP]);

  const handleRSVPSubmitSuccess = () => {
    // Immediately hide the form and show loading while we re-fetch
    setNotFound(false);
    setLoading(true);
    fetchRSVP();
  };

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Guest';

  if (isEditing && rsvp) {
    return (
      <div className="px-8 md:px-16 xl:px-24 py-16 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-12">
          <div>
            <p className="section-label mb-2">Guest Dashboard</p>
            <h1 className="font-serif text-display-md text-faded-navy">Welcome, {displayName}</h1>
            <p className="font-sans text-sm text-stone mt-2">{user?.email}</p>
          </div>
          <button
            onClick={onSignOut}
            className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-sans text-stone hover:text-faded-navy transition-colors duration-300 mt-2"
          >
            <LogOut size={14} />
            Sign Out
          </button>
        </div>
        <RSVPEditForm
          rsvp={rsvp}
          onBack={() => setIsEditing(false)}
          onSaveSuccess={() => {
            setIsEditing(false);
            fetchRSVP();
          }}
        />
      </div>
    );
  }

  return (
    <div className="px-8 md:px-16 xl:px-24 py-16 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-12">
        <div>
          <p className="section-label mb-2">Guest Dashboard</p>
          <h1 className="font-serif text-display-md text-faded-navy">Welcome, {displayName}</h1>
          <p className="font-sans text-sm text-stone mt-2">{user?.email}</p>
        </div>
        <button
          onClick={onSignOut}
          className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-sans text-stone hover:text-faded-navy transition-colors duration-300 mt-2"
        >
          <LogOut size={14} />
          Sign Out
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-24">
          <svg className="animate-spin w-6 h-6 text-warm-sand" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      )}

      {!loading && notFound && (
        <div className="max-w-2xl">
          <div className="border border-border bg-off-white p-6 mb-2">
            <p className="font-serif text-xl text-faded-navy mb-2">Complete Your RSVP</p>
            <p className="font-sans text-sm text-stone leading-relaxed">
              We don't have an RSVP on file for your account yet. Please fill in the form below — your email has been pre-filled so your response will be linked to your account automatically.
            </p>
          </div>
          <div className="border border-border bg-off-white -mt-px">
            <RSVPForm defaultEmail={user?.email} onSubmitSuccess={handleRSVPSubmitSuccess} />
          </div>
        </div>
      )}

      {!loading && rsvp && (
        <div className="space-y-8 max-w-2xl">
          {/* RSVP Status */}
          <div className="border border-border bg-off-white p-6">
            <div className="flex items-center justify-between mb-5">
              <p className="text-xs tracking-[0.25em] uppercase font-sans text-stone">Your RSVP</p>
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-sans text-stone hover:text-faded-navy transition-colors duration-300"
              >
                <Pencil size={12} />
                Edit
              </button>
            </div>
            <div className="flex items-center gap-3 mb-4">
              {rsvp.attending === 'yes' ? (
                <>
                  <CheckCircle size={20} className="text-olive" />
                  <span className="font-serif text-xl text-faded-navy">Attending</span>
                </>
              ) : (
                <>
                  <XCircle size={20} className="text-stone" />
                  <span className="font-serif text-xl text-faded-navy">Not Attending</span>
                </>
              )}
            </div>
            <div className="space-y-2 border-t border-border pt-4">
              <div className="flex justify-between py-1.5">
                <span className="text-sm font-sans text-stone">Name</span>
                <span className="text-sm font-sans text-faded-navy">{rsvp.primary_name}</span>
              </div>
              <div className="flex justify-between py-1.5 border-t border-border">
                <span className="text-sm font-sans text-stone">Email</span>
                <span className="text-sm font-sans text-faded-navy">{rsvp.primary_email || '—'}</span>
              </div>
              {rsvp.submitted_at && (
                <div className="flex justify-between py-1.5 border-t border-border">
                  <span className="text-sm font-sans text-stone">Submitted</span>
                  <span className="text-sm font-sans text-faded-navy">
                    {new Date(rsvp.submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {rsvp.attending === 'yes' && (
            <>
              {/* Dietary */}
              <div className="border border-border bg-off-white p-6">
                <div className="flex items-center gap-2 mb-5">
                  <Utensils size={15} className="text-stone" />
                  <p className="text-xs tracking-[0.25em] uppercase font-sans text-stone">Dietary Requirements</p>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between py-1.5">
                    <span className="text-sm font-sans text-stone">{rsvp.primary_name}</span>
                    <span className="text-sm font-sans text-faded-navy">{dietaryLabel(rsvp.primary_dietary)}</span>
                  </div>
                  {(rsvp.plus_ones || []).map((p, i) => (
                    <div key={i} className="flex justify-between py-1.5 border-t border-border">
                      <span className="text-sm font-sans text-stone">{p.name}</span>
                      <span className="text-sm font-sans text-faded-navy">{dietaryLabel(p.dietary, p.dietaryOther)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Plus Ones */}
              {(rsvp.plus_ones || []).length > 0 && (
                <div className="border border-border bg-off-white p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <Users size={15} className="text-stone" />
                    <p className="text-xs tracking-[0.25em] uppercase font-sans text-stone">Your Party</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 py-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-faded-navy/40" />
                      <span className="text-sm font-sans text-faded-navy">{rsvp.primary_name} <span className="text-stone text-xs">(you)</span></span>
                    </div>
                    {(rsvp.plus_ones || []).map((p, i) => (
                      <div key={i} className="flex items-center gap-2 py-1.5 border-t border-border">
                        <div className="w-1.5 h-1.5 rounded-full bg-faded-navy/40" />
                        <span className="text-sm font-sans text-faded-navy">{p.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Weekend Events */}
              <div className="border border-border bg-off-white p-6">
                <div className="flex items-center gap-2 mb-5">
                  <Calendar size={15} className="text-stone" />
                  <p className="text-xs tracking-[0.25em] uppercase font-sans text-stone">Your Weekend Schedule</p>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Friday Evening Apéritif', sublabel: 'Galions Quay · 17:00', active: rsvp.friday_evening },
                    { label: 'Saturday Wedding', sublabel: 'St Petrox Church & Gullsway Boathouse', active: true },
                    { label: 'Sunday Morning on the Beach', sublabel: 'Blackpool Sands · 10:00', active: rsvp.sunday_brunch },
                    { label: 'Sunday Afternoon Walk', sublabel: 'South West Coast Path · 15:00', active: rsvp.sunday_walk },
                  ].map((event) => (
                    <div key={event.label} className="flex items-start justify-between gap-4 py-2 border-b border-border last:border-0">
                      <div>
                        <p className={`text-sm font-sans ${event.active ? 'text-faded-navy' : 'text-stone/50'}`}>{event.label}</p>
                        <p className={`text-xs font-sans mt-0.5 ${event.active ? 'text-stone' : 'text-stone/40'}`}>{event.sublabel}</p>
                      </div>
                      <span className={`text-xs tracking-[0.15em] uppercase font-sans px-2.5 py-1 border shrink-0 ${
                        event.active
                          ? 'border-olive text-olive bg-olive/5' : 'border-stone/20 text-stone/40 bg-transparent'
                      }`}>
                        {event.active ? 'Attending' : 'Not attending'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Message */}
              {rsvp.message && (
                <div className="border border-border bg-off-white p-6">
                  <p className="text-xs tracking-[0.25em] uppercase font-sans text-stone mb-4">Your Message</p>
                  <p className="font-sans text-sm text-faded-navy leading-relaxed italic">"{rsvp.message}"</p>
                </div>
              )}
            </>
          )}

          <button
            onClick={() => setIsEditing(true)}
            className="w-full py-3.5 text-xs tracking-[0.3em] uppercase font-sans border border-faded-navy text-faded-navy hover:bg-faded-navy hover:text-buttermilk transition-all duration-300 flex items-center justify-center gap-2"
          >
            <Pencil size={12} />
            Edit My RSVP
          </button>
        </div>
      )}
    </div>
  );
}
